import { Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { colors } from "#app/theme/v2/colors";
import { spacing } from "#app/theme/v2/spacing";
import { Loader } from "#components/atoms/Loader/Loader";
import { Text } from "#components/atoms/Typographies/Text";
import {
  QuoteRequestLineAssemblyPrice,
  QuoteRequestLineComputedPrice,
} from "#modules/quoteRequestLinesV2/domain/buildQuoteRequestLineTableRows";
import { NO_PRICE_PLACEHOLDER } from "#modules/quoteRequestLinesV2/domain/getComputedQuoteRequestLinePrice";
import { Flex } from "#shared/view/components/Flex";
import { formatPrice } from "shared";

type Props = {
  depth: number;
  isAssemblyParent: boolean;
  linePrice: QuoteRequestLineComputedPrice | null;
  assemblyPrice: QuoteRequestLineAssemblyPrice | null;
  isAnalyzingOrPredicting: boolean;
};

export const TotalPriceCell = ({
  depth,
  isAssemblyParent,
  linePrice,
  assemblyPrice,
  isAnalyzingOrPredicting,
}: Props) => {
  const { t } = useTranslation();

  if (isAnalyzingOrPredicting) {
    return (
      <Flex justify="flex-end" data-testid="total-price-loading">
        <Loader size="small" />
      </Flex>
    );
  }

  if (depth > 0) {
    return (
      <Box ta="right">
        <Text size="small" color={linePrice ? undefined : colors.text.secondaryDisabled}>
          {linePrice
            ? `${formatPrice(linePrice.assembly.totalPrice)}${t("quoteRequestV2.assembly.perPieceSuffix")}`
            : NO_PRICE_PLACEHOLDER}
        </Text>
      </Box>
    );
  }

  return (
    <Flex direction="column" gap={spacing[0.5]} align="flex-end">
      <Text size="small" color={linePrice ? undefined : colors.text.secondaryDisabled}>
        {linePrice
          ? formatPrice(assemblyPrice ? assemblyPrice.seriesPrice : linePrice.seriesPrice)
          : NO_PRICE_PLACEHOLDER}
      </Text>
      {isAssemblyParent && linePrice && (
        <Text size="tiny" type="regular" color={colors.text.secondaryDefault}>
          {t("quoteRequestV2.costing.assemblyPortion", {
            price: formatPrice(linePrice.seriesPrice),
          })}
        </Text>
      )}
    </Flex>
  );
};
